<template>
  <div class="page-header">
    <h2>Gestion des Pays, Villes & Adresses</h2>
    <button
      v-if="activeTab === 'addresses'"
      @click="openCreateAddressModal"
      class="btn btn-primary"
    >
      + Nouvelle adresse
    </button>
    <button
      v-if="activeTab === 'cities'"
      @click="openCreateCityModal"
      class="btn btn-primary"
    >
      + Nouvelle ville
    </button>
    <button
      v-if="activeTab === 'countries'"
      @click="openCreateCountryModal"
      class="btn btn-primary"
    >
      + Nouveau pays
    </button>
  </div>

  <!-- Messages d'info -->
  <InfoBox
    v-if="infoMessage"
    v-model:open="infoOpen"
    :type="infoType"
    @close="infoOpen = false; infoMessage = ''"
  >
    {{ infoMessage }}
  </InfoBox>

  <!-- Onglets + Filtres -->
  <div class="filters form-group">
    <div class="btn-group">
      <button
        @click="activeTab = 'addresses'"
        :class="['btn btn-chip', { 'is-active': activeTab === 'addresses' }]"
      >
        Adresses
      </button>
      <button
        @click="activeTab = 'cities'"
        :class="['btn btn-chip', { 'is-active': activeTab === 'cities' }]"
      >
        Villes
      </button>
      <button
        @click="activeTab = 'countries'"
        :class="['btn btn-chip', { 'is-active': activeTab === 'countries' }]"
      >
        Pays
      </button>
    </div>

    <!-- Filtres Adresses -->
    <div class="btn-group-if" v-if="activeTab === 'addresses'">
      <div class="btn-group">
        <div class="filters-group">
          <label>Nom</label>
          <input
            v-model="addressFilters.name"
            type="text"
            placeholder="Rechercher..."
          />
        </div>
        <div class="filters-group">
          <label>Ville</label>
          <input
            v-model="addressFilters.city"
            type="text"
            placeholder="Rechercher..."
          />
        </div>
        <div class="filters-group">
          <label>Rue</label>
          <input
            v-model="addressFilters.street"
            type="text"
            placeholder="Rechercher..."
          />
        </div>
      </div>
      <div class="btn-group">
        <span class="badge badge-info">{{ filteredAddresses.length }} adresse(s) trouvée(s) sur {{ allAddresses.length }}</span>
        <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
      </div>
    </div>

    <!-- Filtres Villes -->
    <div class="btn-group-if" v-if="activeTab === 'cities'">
      <div class="btn-group">
        <div class="filters-group">
          <label>Nom</label>
          <input
            v-model="cityFilters.name"
            type="text"
            placeholder="Rechercher..."
          />
        </div>
        <div class="filters-group">
          <label>Pays</label>
          <select v-model="cityFilters.countryId">
            <option value="">-- Tous les pays --</option>
            <option
              v-for="country in countries"
              :key="country.id"
              :value="country.id"
            >
              {{ country.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="btn-group">
        <span class="badge badge-info">{{ filteredCities.length }} ville(s) trouvée(s) sur {{ allCities.length }}</span>
        <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
      </div>
    </div>

    <!-- Filtres Pays -->
    <div class="btn-group-if" v-if="activeTab === 'countries'">
      <div class="btn-group">
        <div class="filters-group">
          <label>Nom</label>
          <input
            v-model="countryFilters.name"
            type="text"
            placeholder="Rechercher..."
          />
        </div>
        <div class="filters-group">
          <label>Code ISO</label>
          <input
            v-model="countryFilters.code"
            type="text"
            placeholder="FR, BE, CH..."
          />
        </div>
      </div>
      <div class="btn-group">
        <span class="badge badge-info">{{ filteredCountries.length }} pays trouvé(s) sur {{ allCountries.length }}</span>
        <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
      </div>
    </div>
  </div>

  <!-- TAB: ADRESSES -->
  <div class="address-content" v-if="activeTab === 'addresses'">
    <table v-if="!loading && allAddresses.length > 0" class="table">
      <thead>
        <tr>
          <th @click="sortAddressesBy('name')" class="th--sortable">
            Nom <span class="sort-icon">{{ getAddressSortIcon('name') }}</span>
          </th>
          <th @click="sortAddressesBy('street')" class="th--sortable">
            Rue <span class="sort-icon">{{ getAddressSortIcon('street') }}</span>
          </th>
          <th @click="sortAddressesBy('postalCode')" class="th--sortable">
            Code Postal <span class="sort-icon">{{ getAddressSortIcon('postalCode') }}</span>
          </th>
          <th @click="sortAddressesBy('city')" class="th--sortable">
            Ville <span class="sort-icon">{{ getAddressSortIcon('city') }}</span>
          </th>
          <th>Complément</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="address in visibleAddresses" :key="address.id" class="row">
          <td><strong>{{ address.name }}</strong></td>
          <td>{{ address.street }}</td>
          <td>{{ address.postalCode }}</td>
          <td>{{ address.city?.name || 'N/A' }}</td>
          <td><span class="muted">{{ address.complement || '-' }}</span></td>
          <td>
            <button
              @click="openEditAddressModal(address)"
              class="icon-btn"
              title="Modifier"
            >
              ✏️
            </button>
            <button
              @click="openDeleteAddressModal(address)"
              class="icon-btn danger"
              title="Supprimer"
            >
              🗑
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <EmptyState
      v-if="!loading && filteredAddresses.length === 0 && allAddresses.length > 0"
      :is-empty="true"
      emptyMessage="Aucune adresse ne correspond aux filtres"
    />

    <div
      v-if="!loading && visibleAddresses.length > 0 && visibleAddressCount < filteredAddresses.length"
      class="btn-group center"
    >
      <button @click="loadMoreAddresses" class="btn">- Charger plus -</button>
    </div>

    <EmptyState
      v-if="!loading && allAddresses.length === 0"
      :is-empty="true"
      emptyMessage="Aucune adresse pour le moment"
    />
  </div>

  <!-- TAB: VILLES -->
  <div v-if="activeTab === 'cities'">
    <table v-if="!loading && allCities.length > 0" class="table">
      <thead>
        <tr>
          <th @click="sortCitiesBy('name')" class="th--sortable">
            Nom <span class="sort-icon">{{ getCitySortIcon('name') }}</span>
          </th>
          <th @click="sortCitiesBy('slug')" class="th--sortable">
            Slug <span class="sort-icon">{{ getCitySortIcon('slug') }}</span>
          </th>
          <th @click="sortCitiesBy('country')" class="th--sortable">
            Pays <span class="sort-icon">{{ getCitySortIcon('country') }}</span>
          </th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="city in visibleCities" :key="city.id" class="row">
          <td>{{ city.name }}</td>
          <td><span class="mono">{{ city.slug }}</span></td>
          <td>{{ city.country?.name || 'N/A' }}</td>
          <td>
            <button
              @click="openEditCityModal(city)"
              class="icon-btn"
              title="Modifier"
            >
              ✏️
            </button>
            <button
              @click="openDeleteCityModal(city)"
              class="icon-btn danger"
              title="Supprimer"
            >
              🗑
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <EmptyState
      v-if="!loading && filteredCities.length === 0 && allCities.length > 0"
      :is-empty="true"
      emptyMessage="Aucune ville ne correspond aux filtres"
    />

    <div
      v-if="!loading && visibleCities.length > 0 && visibleCityCount < filteredCities.length"
      class="btn-group center"
    >
      <button @click="loadMoreCities" class="btn btn--primary">Charger plus</button>
    </div>

    <EmptyState
      v-if="!loading && allCities.length === 0"
      :is-empty="true"
      emptyMessage="Aucune ville pour le moment"
    />
  </div>

  <!-- TAB: PAYS -->
  <div v-if="activeTab === 'countries'">
    <table v-if="!loading && allCountries.length > 0" class="table">
      <thead>
        <tr>
          <th @click="sortCountriesBy('name')" class="th--sortable">
            Nom <span class="sort-icon">{{ getCountrySortIcon('name') }}</span>
          </th>
          <th @click="sortCountriesBy('code')" class="th--sortable">
            Code ISO <span class="sort-icon">{{ getCountrySortIcon('code') }}</span>
          </th>
          <th>Indicatif</th>
          <th>Devise</th>
          <th>Taux</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="country in visibleCountries" :key="country.id" class="row">
          <td><strong>{{ country.name }}</strong></td>
          <td><span class="mono">{{ country.code || 'N/A' }}</span></td>
          <td><span class="muted">{{ country.phoneCode || '-' }}</span></td>
          <td><span class="mono">{{ country.currency || '-' }}</span></td>
          <td><span class="muted">{{ country.exchangeRate ?? '-' }}</span></td>
          <td>
            <button
              @click="openEditCountryModal(country)"
              class="icon-btn"
              title="Modifier"
            >
              ✏️
            </button>
            <button
              @click="openDeleteCountryModal(country)"
              class="icon-btn danger"
              title="Supprimer"
            >
              🗑
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <EmptyState
      v-if="!loading && filteredCountries.length === 0 && allCountries.length > 0"
      :is-empty="true"
      emptyMessage="Aucun pays ne correspond aux filtres"
    />

    <div
      v-if="!loading && visibleCountries.length > 0 && visibleCountryCount < filteredCountries.length"
      class="btn-group center"
    >
      <button @click="loadMoreCountries" class="btn">- Charger plus -</button>
    </div>

    <EmptyState
      v-if="!loading && allCountries.length === 0"
      :is-empty="true"
      emptyMessage="Aucun pays pour le moment"
    />
  </div>
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useModalStore } from '@/stores/modalStore'
import { useCountriesStore } from '@/stores/countriesStore'
import FormFieldsCity from '@/components/FormFields/FormFieldsCity.vue'
import FormFieldsAddress from '@/components/FormFields/FormFieldsAddress.vue'
import FormFieldsCountry from '@/components/FormFields/FormFieldsCountry.vue'

const emit = defineEmits(['loading'])
const toast = useToast()
const modalStore = useModalStore()
const countriesStore = useCountriesStore()

const activeTab = ref('addresses')
const allCities = ref([])
const allAddresses = ref([])
const allCountries = ref([])
const countries = ref([])
const loading = ref(false)
const error = ref('')

const visibleCityCount = ref(10)
const visibleAddressCount = ref(10)
const visibleCountryCount = ref(10)
const ITEMS_STEP = 10

const citySortKey = ref('name')
const citySortOrder = ref('asc')
const addressSortKey = ref('name')
const addressSortOrder = ref('asc')
const countrySortKey = ref('name')
const countrySortOrder = ref('asc')

const cityFilters = ref({ name: '', countryId: '' })
const addressFilters = ref({ name: '', city: '', street: '' })
const countryFilters = ref({ name: '', code: '' })

const resetCityFilters = () => {
  cityFilters.value = { name: '', countryId: '' }
  visibleCityCount.value = ITEMS_STEP
}

const resetAddressFilters = () => {
  addressFilters.value = { name: '', city: '', street: '' }
  visibleAddressCount.value = ITEMS_STEP
}

const resetCountryFilters = () => {
  countryFilters.value = { name: '', code: '' }
  visibleCountryCount.value = ITEMS_STEP
}

const filteredCities = computed(() => {
  return allCities.value.filter(city => {
    if (cityFilters.value.name) {
      const term = cityFilters.value.name.toLowerCase()
      const name = city.name?.toLowerCase() || ''
      const slug = city.slug?.toLowerCase() || ''
      if (!name.includes(term) && !slug.includes(term)) return false
    }
    if (cityFilters.value.countryId) {
      if (city.country?.id !== parseInt(cityFilters.value.countryId)) return false
    }
    return true
  })
})

const filteredAddresses = computed(() => {
  return allAddresses.value.filter(address => {
    if (addressFilters.value.name) {
      const term = addressFilters.value.name.toLowerCase()
      if (!(address.name?.toLowerCase() || '').includes(term)) return false
    }
    if (addressFilters.value.city) {
      const term = addressFilters.value.city.toLowerCase()
      if (!(address.city?.name?.toLowerCase() || '').includes(term)) return false
    }
    if (addressFilters.value.street) {
      const term = addressFilters.value.street.toLowerCase()
      if (!(address.street?.toLowerCase() || '').includes(term)) return false
    }
    return true
  })
})

const filteredCountries = computed(() => {
  return allCountries.value.filter(country => {
    if (countryFilters.value.name) {
      const term = countryFilters.value.name.toLowerCase()
      if (!(country.name?.toLowerCase() || '').includes(term)) return false
    }
    if (countryFilters.value.code) {
      const term = countryFilters.value.code.toLowerCase()
      if (!(country.code?.toLowerCase() || '').includes(term)) return false
    }
    return true
  })
})

const sortedCities = computed(() => {
  const cities = [...filteredCities.value]
  cities.sort((a, b) => {
    let aVal, bVal
    switch (citySortKey.value) {
      case 'name': aVal = a.name || ''; bVal = b.name || ''; break
      case 'slug': aVal = a.slug || ''; bVal = b.slug || ''; break
      case 'country': aVal = a.country?.name || ''; bVal = b.country?.name || ''; break
      default: return 0
    }
    if (aVal < bVal) return citySortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return citySortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  return cities
})

const sortedAddresses = computed(() => {
  const addresses = [...filteredAddresses.value]
  addresses.sort((a, b) => {
    let aVal, bVal
    switch (addressSortKey.value) {
      case 'name': aVal = a.name || ''; bVal = b.name || ''; break
      case 'street': aVal = a.street || ''; bVal = b.street || ''; break
      case 'postalCode': aVal = a.postalCode || ''; bVal = b.postalCode || ''; break
      case 'city': aVal = a.city?.name || ''; bVal = b.city?.name || ''; break
      default: return 0
    }
    if (aVal < bVal) return addressSortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return addressSortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  return addresses
})

const sortedCountries = computed(() => {
  const countriesList = [...filteredCountries.value]
  countriesList.sort((a, b) => {
    let aVal, bVal
    switch (countrySortKey.value) {
      case 'name': aVal = a.name || ''; bVal = b.name || ''; break
      case 'code': aVal = a.code || ''; bVal = b.code || ''; break
      default: return 0
    }
    if (aVal < bVal) return countrySortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return countrySortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  return countriesList
})

const visibleCities = computed(() => sortedCities.value.slice(0, visibleCityCount.value))
const visibleAddresses = computed(() => sortedAddresses.value.slice(0, visibleAddressCount.value))
const visibleCountries = computed(() => sortedCountries.value.slice(0, visibleCountryCount.value))

const sortCitiesBy = (key) => {
  if (citySortKey.value === key) {
    citySortOrder.value = citySortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    citySortKey.value = key
    citySortOrder.value = 'asc'
  }
}

const sortAddressesBy = (key) => {
  if (addressSortKey.value === key) {
    addressSortOrder.value = addressSortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    addressSortKey.value = key
    addressSortOrder.value = 'asc'
  }
}

const sortCountriesBy = (key) => {
  if (countrySortKey.value === key) {
    countrySortOrder.value = countrySortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    countrySortKey.value = key
    countrySortOrder.value = 'asc'
  }
}

const getCitySortIcon = (key) => citySortKey.value !== key ? '⇅' : (citySortOrder.value === 'asc' ? '↑' : '↓')
const getAddressSortIcon = (key) => addressSortKey.value !== key ? '⇅' : (addressSortOrder.value === 'asc' ? '↑' : '↓')
const getCountrySortIcon = (key) => countrySortKey.value !== key ? '⇅' : (countrySortOrder.value === 'asc' ? '↑' : '↓')

const loadMoreCities = () => { if (visibleCityCount.value < sortedCities.value.length) visibleCityCount.value += ITEMS_STEP }
const loadMoreAddresses = () => { if (visibleAddressCount.value < sortedAddresses.value.length) visibleAddressCount.value += ITEMS_STEP }
const loadMoreCountries = () => { if (visibleCountryCount.value < sortedCountries.value.length) visibleCountryCount.value += ITEMS_STEP }

onMounted(async () => await loadData())

const loadData = async () => {
  loading.value = true
  emit('loading', true)
  error.value = ''

  try {
    const [citiesRes, addressesRes, countriesRes] = await Promise.all([
      fetch(config.apiUrl + '/cities', { credentials: 'include' }),
      fetch(config.apiUrl + '/addresses', { credentials: 'include' }),
      fetch(config.apiUrl + '/countries', { credentials: 'include' })
    ])

    if (citiesRes.ok) allCities.value = await citiesRes.json()
    if (addressesRes.ok) allAddresses.value = await addressesRes.json()
    if (countriesRes.ok) {
      const data = await countriesRes.json()
      countries.value = data
      allCountries.value = data
    }

    visibleCityCount.value = ITEMS_STEP
    visibleAddressCount.value = ITEMS_STEP
    visibleCountryCount.value = ITEMS_STEP
  } catch (err) {
    error.value = 'Erreur lors du chargement'
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 PAYS
const openCreateCountryModal = async () => {
  const { result } = modalStore.openComponent(
    FormFieldsCountry,
    { formData: { name: '', code: '', phoneCode: '', currency: '', exchangeRate: null } },
    { title: 'Créer un pays', maxWidth: '600px', kind: 'component' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveCountry(res.data, false)
}

const openEditCountryModal = async (country) => {
  const { result } = modalStore.openComponent(
    FormFieldsCountry,
    { formData: { name: country.name, code: country.code || '', phoneCode: country.phoneCode || '', currency: country.currency || '', exchangeRate: country.exchangeRate ?? null } },
    { title: 'Modifier le pays', maxWidth: '600px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveCountry(res.data, true, country.id)
}

const openDeleteCountryModal = async (country) => {
  const { result } = modalStore.open({
    kind: 'confirm',
    title: 'Confirmer la suppression',
    message: `Êtes-vous sûr de vouloir supprimer ce pays ?

Nom: ${country.name}
Code: ${country.code || 'N/A'}

⚠️ Toutes les villes liées seront également affectées.`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler'
  })
  const res = await result
  if (res.action === 'confirm') await handleDeleteCountry(country.id)
}

const handleSaveCountry = async (formData, isEdit, id = null) => {
  if (!formData.name) {
    toast.error('Le nom est requis')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    const url = isEdit ? `${config.apiUrl}/country/${id}` : config.apiUrl + '/country'
    const method = isEdit ? 'PUT' : 'POST'

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(formData)
    })

    if (res.ok) {
      await loadData()
      await countriesStore.forceRefetch()
      toast.success(isEdit ? 'Pays modifié' : 'Pays créé')
    } else {
      toast.error('Erreur lors de l\'opération')
    }
  } catch (err) {
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const handleDeleteCountry = async (id) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/country/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (res.ok) {
      await loadData()
      await countriesStore.forceRefetch()
      toast.success('Pays supprimé')
    } else {
      toast.error('Erreur lors de la suppression')
    }
  } catch (err) {
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 VILLES
const openCreateCityModal = async () => {
  const { result } = modalStore.openComponent(
    FormFieldsCity,
    { formData: { name: '', slug: '', countryId: '' }, countries: countries.value },
    { title: 'Créer une ville', maxWidth: '600px', kind: 'component' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveCity(res.data, false)
}

const openEditCityModal = async (city) => {
  const { result } = modalStore.openComponent(
    FormFieldsCity,
    { formData: { name: city.name, slug: city.slug, countryId: city.country?.id || '' }, countries: countries.value },
    { title: 'Modifier la ville', maxWidth: '600px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveCity(res.data, true, city.id)
}

const openDeleteCityModal = async (city) => {
  const { result } = modalStore.open({
    kind: 'confirm',
    title: 'Confirmer la suppression',
    message: `Êtes-vous sûr de vouloir supprimer cette ville ?

Nom: ${city.name}
Slug: ${city.slug}
Pays: ${city.country?.name}

⚠️ Toutes les adresses liées seront également affectées.`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler'
  })
  const res = await result
  if (res.action === 'confirm') await handleDeleteCity(city.id)
}

const handleSaveCity = async (formData, isEdit, id = null) => {
  if (!formData.name || !formData.slug || !formData.countryId) {
    toast.error('Nom, slug et pays sont requis')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    const url = isEdit ? `${config.apiUrl}/city/${id}` : config.apiUrl + '/city'
    const method = isEdit ? 'PUT' : 'POST'

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(formData)
    })

    if (res.ok) {
      await loadData()
      toast.success(isEdit ? 'Ville modifiée' : 'Ville créée')
    } else {
      toast.error('Erreur lors de l\'opération')
    }
  } catch (err) {
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const handleDeleteCity = async (id) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/city/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (res.ok) {
      await loadData()
      toast.success('Ville supprimée')
    } else {
      toast.error('Erreur lors de la suppression')
    }
  } catch (err) {
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 ADRESSES
const openCreateAddressModal = async () => {
  const { result } = modalStore.openComponent(
    FormFieldsAddress,
    { formData: { name: '', street: '', postalCode: '', cityId: '', complement: '', thumbnailFile:
      null }, cities: allCities.value },
    { title: 'Créer une adresse', maxWidth: '600px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveAddress(res.data, false)
}

const openEditAddressModal = async (address) => {
  const { result } = modalStore.openComponent(
    FormFieldsAddress,
    {
      formData: {
        name: address.name,
        street: address.street,
        postalCode: address.postalCode,
        cityId: address.city?.id || '',
        complement: address.complement || '',
        thumbnailName: address.thumbnailName || null,
        thumbnailFile: null
      },
      cities: allCities.value
    },
    { title: 'Modifier l\'adresse', maxWidth: '600px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveAddress(res.data, true, address.id)
}

const openDeleteAddressModal = async (address) => {
  const { result } = modalStore.open({
    kind: 'confirm',
    title: 'Confirmer la suppression',
    message: `Êtes-vous sûr de vouloir supprimer cette adresse ?

Nom: ${address.name}
Rue: ${address.street}
Ville: ${address.city?.name}`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler'
  })
  const res = await result
  if (res.action === 'confirm') await handleDeleteAddress(address.id)
}

const handleSaveAddress = async (formData, isEdit, id = null) => {
  if (!formData.name || !formData.street || !formData.postalCode || !formData.cityId) {
    toast.error('Nom, rue, code postal et ville sont requis')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    const hasFile = formData.thumbnailFile && formData.thumbnailFile instanceof File
    const shouldDeleteThumbnail = isEdit && formData.thumbnailName === null && !hasFile

    // 1️⃣ Créer ou mettre à jour l'adresse (sans le fichier)
    const url = isEdit ? `${config.apiUrl}/address/${id}` : config.apiUrl + '/address'
    const method = isEdit ? 'PUT' : 'POST'

    const addressData = {
      name: formData.name,
      street: formData.street,
      postalCode: formData.postalCode,
      cityId: formData.cityId,
      complement: formData.complement || null
    }

    if (formData.latitude) addressData.latitude = formData.latitude
    if (formData.longitude) addressData.longitude = formData.longitude

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(addressData)
    })

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}))
      toast.error(errorData.error || 'Erreur lors de l\'opération')
      return
    }

    // Récupérer l'ID de l'adresse (nouveau ou existant)
    const savedAddress = await res.json()
    const addressId = isEdit ? id : savedAddress.id

    // 2️⃣ Supprimer le thumbnail si demandé
    if (shouldDeleteThumbnail) {
      const deleteRes = await fetch(`${config.apiUrl}/address/${addressId}/thumbnail`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (!deleteRes.ok) {
        toast.warning('Adresse sauvegardée mais erreur lors de la suppression du thumbnail')
      }
    }

    // 3️⃣ Upload du nouveau thumbnail si présent
    if (hasFile) {
      const formDataObj = new FormData()
      formDataObj.append('thumbnail', formData.thumbnailFile)

      const uploadRes = await fetch(`${config.apiUrl}/address/${addressId}/thumbnail`, {
        method: 'POST',
        credentials: 'include',
        body: formDataObj
      })

      if (!uploadRes.ok) {
        const errorData = await uploadRes.json().catch(() => ({}))
        toast.warning(errorData.error || 'Adresse sauvegardée mais erreur lors de l\'upload du thumbnail')
      }
    }

    await loadData()
    toast.success(isEdit ? 'Adresse modifiée' : 'Adresse créée')

  } catch (err) {
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}



const handleDeleteAddress = async (id) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/address/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (res.ok) {
      await loadData()
      toast.success('Adresse supprimée')
    } else {
      toast.error('Erreur lors de la suppression')
    }
  } catch (err) {
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const resetFilters = () => {
  resetAddressFilters()
  resetCityFilters()
  resetCountryFilters()
}
</script>

<style scoped>
.filters.form-group > .btn-group:first-child {
  justify-content: flex-start !important;
}
.btn-group-if {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}
.address-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
</style>
